import bootstrapper from '../bootstrapper';
import {
  displayFlashErrorMessage,
  handleScroll,
  hideFloatingErrorMessage,
  removeFlashErrorMessage,
  showFloatingErrorMessage,
  installmentsNumber,
  disableItems,
  addPaymentHelp,
} from './checkoutHelpers';
import { clientSideFailedPaymentPayload, createFailedPayment } from './createFailedPayment';
import PaymentSubmission from './paymentSubmission';
import { track } from '../analytics/seshat/index';

let stripe;
let checkout;
let checkoutSessionPaymentInfo;

const paymentElementEl = () => document.getElementById('payment-element');

const payNowButtonEl = () => document.querySelector(
  '[data-enrollment-engine--checkout-target="payNowButton"]',
);

export const stripeCheckoutSessionEnabled = () =>
  paymentElementEl()?.dataset?.checkoutSessionEnabled === 'true';

const checkoutSessionClientSecret = () => paymentElementEl()?.dataset?.clientSecret;

const updateChargeAmount = async (paymentMethod, paymentInstallmentId, paymentId) => {
  const paymentSubmission = new PaymentSubmission(
    paymentMethod,
    paymentInstallmentId,
    paymentId,
  );
  paymentSubmission.reload();
};

const enablePaymentMethodSwitch = async (res) => {
  const paymentMethodSwitches = document.querySelectorAll('[id^="switch-to-bank-debit"]');

  paymentMethodSwitches.forEach((paymentMethodSwitch) => {
    paymentMethodSwitch.value = res.available_alternative_payment_method;

    if (!paymentMethodSwitch.classList.contains('d-none')) {
      return;
    }

    paymentMethodSwitch.classList.remove('d-none');

    paymentMethodSwitch.addEventListener('click', () => {
      track({
        eventName: 'click_payment_method_switch',
        options: {
          order_id: res.order_id,
          payment_installment_id: res.payment_installment_id,
          payment_id: res.payment_id,
        },
      });
    });

    paymentMethodSwitch.addEventListener('click', (e) => {
      hideFloatingErrorMessage();
      window.removeEventListener('scroll', handleScroll);
      removeFlashErrorMessage();
      updateChargeAmount(
        e.target.value,
        checkoutSessionPaymentInfo.payment_installment_id,
        checkoutSessionPaymentInfo.payment_id,
      );
    });
  });

  document.cookie = 'payment_method_switch_displayed=true; path=/';
  track({
    eventName: 'show_payment_method_switch',
    options: {
      order_id: res.order_id,
      payment_installment_id: res.payment_installment_id,
      payment_id: res.payment_id,
    },
  });
};

const addEventListenerToPaymentHelp = (paymentHelp, res) => {
  paymentHelp.addEventListener('click', () => {
    const modalContainer = document.getElementById('payment-support-modal-container');
    modalContainer.classList.remove('d-none');
    track({
      eventName: 'click_payment_help_button',
      options: {
        order_id: res.order_id,
        payment_installment_id: res.payment_installment_id,
        payment_id: res.payment_id,
      },
    });
  });
  paymentHelp.classList.remove('d-none');
};

const enablePaymentHelp = (res) => {
  const fixedPaymentHelp = document.getElementById('payment-help-fixed');
  addEventListenerToPaymentHelp(fixedPaymentHelp, res);

  if (res.selected_payment_method === 'sepa_debit' || res.available_alternative_payment_method === 'sepa_debit') {
    const floatingPaymentHelp = document.getElementById('payment-help-floating');
    addEventListenerToPaymentHelp(floatingPaymentHelp, res);
  }
};

const createFailedPaymentWithError = async (error) => {
  const body = clientSideFailedPaymentPayload('stripe_intent', error, null, installmentsNumber());
  await createFailedPayment(body).then(res => res.json())
    .then((res) => {
      displayFlashErrorMessage(res.error_message);
      if (res.selected_payment_method === 'sepa_debit') {
        showFloatingErrorMessage();
        window.addEventListener('scroll', handleScroll);
        handleScroll();
      }
      if (res.allow_payment_method_switch === true) {
        enablePaymentMethodSwitch(res);
      }

      if (res.allow_payment_help === true) {
        addPaymentHelp(res);
        enablePaymentHelp(res);
      }

      if (res.allow_payment_method_switch === true && res.allow_payment_help === true) {
        const spanTexts = document.querySelectorAll('[id^="or-text"]');
        spanTexts.forEach((spanText) => {
          spanText.classList.remove('d-none');
        });
      }
    });
};

const initializeCheckoutSession = (clientSecret) => {
  if (typeof stripe.initCheckoutElementsSdk === 'function') {
    return stripe.initCheckoutElementsSdk({ clientSecret });
  }

  if (typeof stripe.initCheckout === 'function') {
    return stripe.initCheckout({ clientSecret });
  }

  throw new Error('Loaded Stripe.js does not support Checkout Session Elements');
};

const mountCheckoutSessionElement = (clientSecret) => {
  checkout = initializeCheckoutSession(clientSecret);
  const paymentElement = checkout.createPaymentElement();
  paymentElement.mount('#payment-element');
};

const buildCheckoutSessionPaymentInfo = () => ({
  client_secret: checkoutSessionClientSecret(),
  payment_installment_id: paymentElementEl()?.dataset?.paymentInstallmentId,
  payment_id: paymentElementEl()?.dataset?.paymentId,
  checkout_session_id: paymentElementEl()?.dataset?.checkoutSessionId,
});

export const stripeCheckoutSessionLoadForm = async () => {
  try {
    if (!stripeCheckoutSessionEnabled() || !checkoutSessionClientSecret()) {
      return;
    }

    stripe = window.Stripe(
      bootstrapper.stripePublishableKey,
      { locale: bootstrapper.locale },
    );
    checkoutSessionPaymentInfo = buildCheckoutSessionPaymentInfo();
    mountCheckoutSessionElement(checkoutSessionClientSecret());
  } catch (error) {
    displayFlashErrorMessage(error.message);
    disableItems();
  }
};

export const rebuildCheckoutSessionElement = async () => stripeCheckoutSessionLoadForm();

const reenablePayNowButton = () => {
  const payNowButton = payNowButtonEl();

  if (payNowButton) {
    payNowButton.classList.remove('disabled');
  }
};

const confirmCheckoutSessionPayment = async () => {
  const loadActionsResult = await checkout.loadActions();

  if (loadActionsResult.type === 'error') {
    await createFailedPaymentWithError(loadActionsResult.error);
    reenablePayNowButton();
    return;
  }

  const { actions } = loadActionsResult;
  const confirmResult = await actions.confirm();

  if (confirmResult.type === 'error') {
    await createFailedPaymentWithError(confirmResult.error);
    reenablePayNowButton();
  }
};

export const payStripeCheckoutSession = () => {
  const payNowButton = payNowButtonEl();

  if (payNowButton) {
    payNowButton.classList.add('disabled');
  }

  if (!checkout) {
    return Promise.resolve();
  }

  return confirmCheckoutSessionPayment();
};
